import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import type { RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import type { MenuProps } from 'antd';
import { App, Button, ConfigProvider, Result, Watermark } from 'antd';
import React, { useCallback, useRef, useState } from 'react';
import {
  AvatarDropdown,
  AvatarName,
  Footer,
  Question,
  SelectLang,
} from '@/components';
import MenuSearch from '@/components/MenuSearch';
import {
  collectSubmenuKeys,
  filterMenuTree,
} from '@/components/MenuSearch/filterMenuItems';
import { SessionManagerWrapper } from '@/components/session';
import { authService } from '@/services/auth/authService';
import defaultSettings from '../config/defaultSettings';
import appConfig from './config/appConfig';
import './app.less';

const loginPath = '/user/login';

/**
 * @name rootContainer
 * @description Wrap the entire app with providers
 * This runs before any page renders
 */
export function rootContainer(container: React.ReactElement) {
  // Configuration for session management
  const sessionConfig = {
    tokenRefreshThreshold: 30 * 1000, // 30 seconds - threshold for showing warnings (NOT for refresh)
    idleTimeout: 30 * 60 * 1000, // 30 minutes - SESSION IDLE TIMEOUT (session terminates after this inactivity)
    idleWarningTime: 2 * 60 * 1000, // 2 minutes warning before idle timeout
    sessionWarningTime: 2 * 60 * 1000, // 2 minutes warning before SESSION IDLE TIMEOUT (NOT token expiration)
    sessionCheckInterval: 60 * 1000, // Check every 60 seconds (balanced frequency)
    enableIdleDetection: true,
    enableTabSync: true,
    enableSilentRefresh: false, // ✅ DISABLED - Token refresh handled by Keycloak's onTokenExpired event only
    enableSessionWarning: true, // Show warning modal when SESSION IDLE TIMER about to expire (not token expiration)
  };

  return (
    <App>
      <SessionManagerWrapper config={sessionConfig}>
        {container}
      </SessionManagerWrapper>
    </App>
  );
}

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      // FAST PATH: Check for authentication (encrypted or plaintext tokens)
      // This provides instant auth check on page load
      const cachedUser = authService.getCachedUser();

      // Check for encrypted tokens (production) or plaintext tokens (development)
      const hasEncryptedToken = localStorage.getItem('encrypted_access_token');
      const hasPlaintextToken = localStorage.getItem('auth_token');
      const hasAnyToken = hasEncryptedToken || hasPlaintextToken;

      if (process.env.NODE_ENV === 'development') {
        console.log('📋 getInitialState: Checking auth state:', {
          hasCachedUser: !!cachedUser,
          hasEncryptedToken: !!hasEncryptedToken,
          hasPlaintextToken: !!hasPlaintextToken,
        });
      }

      // If no token in localStorage (neither encrypted nor plaintext), user is not authenticated
      if (!hasAnyToken) {
        if (process.env.NODE_ENV === 'development') {
          console.log('⚠️ getInitialState: No token found in localStorage');
        }
        return undefined;
      }

      // ⚠️ IMPORTANT: Validate Keycloak session before using cached data
      // If Keycloak session doesn't exist, clear cached data and return undefined
      const isKeycloakAuthenticated = await authService.initializeKeycloak();

      if (!isKeycloakAuthenticated) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            '⚠️ getInitialState: Keycloak session not authenticated, clearing cached data',
          );
        }
        // Clear stale cached data if Keycloak session doesn't exist
        authService.clearAuthData();
        return undefined;
      }

      // If we have both token and cached user, use them (fast path)
      // But only if Keycloak session is valid (checked above)
      if (cachedUser && hasAnyToken) {
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ getInitialState: Using cached user (fast path):', {
            name: cachedUser.name,
            email: cachedUser.email,
            roles: cachedUser.roles?.map((r) => r.name),
            menuItemsCount: cachedUser.pages?.length || 0,
          });
        }

        // Convert to API.CurrentUser format
        const currentUser = {
          id: cachedUser.id,
          name: cachedUser.name,
          email: cachedUser.email,
          avatar: cachedUser.avatar,
          access: cachedUser.roles?.some((role) => role.name === 'admin')
            ? 'admin'
            : 'user',
        } as API.CurrentUser;

        return currentUser;
      }

      // If we have token but no cached user, try to fetch user info
      // This happens rarely (e.g., after cache clear but token still valid)
      if (process.env.NODE_ENV === 'development') {
        console.log(
          '⚠️ getInitialState: Token exists but no cached user, fetching...',
        );
      }

      // Try to get current user (this might fetch from backend)
      const user = await authService.getCurrentUser();

      if (user) {
        const currentUser = {
          id: user.id,
          name: user.name,
          email: user.email,
          avatar: user.avatar,
          access: user.roles?.some((role) => role.name === 'admin')
            ? 'admin'
            : 'user',
        } as API.CurrentUser;

        if (process.env.NODE_ENV === 'development') {
          console.log('✅ getInitialState: User fetched:', currentUser.name);
        }

        return currentUser;
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('⚠️ getInitialState: Failed to get user info');
      }

      return undefined;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ getInitialState: Error fetching user:', error);
      }
      // Don't redirect here - let onPageChange handle it
    }
    return undefined;
  };

  // If not on login page, fetch user and menu
  const { location } = history;
  if (
    ![
      loginPath,
      '/user/register',
      '/user/register-result',
      '/auth/callback',
      '/auth/silent-check',
    ].includes(location.pathname)
  ) {
    const currentUser = await fetchUserInfo();

    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout API support: https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const [menuKeyword, setMenuKeyword] = useState('');
  const openMenuKeysRef = useRef<string[]>([]);
  const isSearchingMenu = menuKeyword.trim().length > 0;

  const handleMenuKeywordChange = useCallback((value: string) => {
    setMenuKeyword(value);
  }, []);

  const handleCollapse = useCallback((collapsed: boolean) => {
    if (collapsed) {
      setMenuKeyword('');
    }
  }, []);

  return {
    className: 'custom-app-layout',
    actionsRender: () => [
      <Question key="doc" />,
      <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    footerRender: () => <Footer />,
    // Custom 403 page (replaces default Chinese page)
    // Must be a render function, not JSX, to avoid React prop warnings
    unAccessible: (
      <Result
        status="403"
        title="403"
        subTitle="Sorry, you are not authorized to access this page."
        extra={
          <Button type="primary" onClick={() => history.push('/')}>
            Back Home
          </Button>
        }
      />
    ),
    onPageChange: () => {
      const { location } = history;

      if (process.env.NODE_ENV === 'development') {
        console.log(
          'onPageChange - Path:',
          location.pathname,
          'Has currentUser:',
          !!initialState?.currentUser,
        );
      }

      // Public routes that don't require authentication
      const publicRoutes = [
        '/user/login',
        '/auth/callback',
        '/auth/silent-check',
      ];

      // Check if current path is public
      const isPublicRoute = publicRoutes.includes(location.pathname);

      // If not logged in and not on public route, redirect to login
      if (!initialState?.currentUser && !isPublicRoute) {
        // Check localStorage for token as backup (in case initialState hasn't updated yet)
        const token = localStorage.getItem('auth_token');
        const cachedUser = localStorage.getItem('current_user');

        if (process.env.NODE_ENV === 'development') {
          console.log(
            'onPageChange - No currentUser in initialState, checking localStorage...',
          );
          console.log(
            'onPageChange - Has token:',
            !!token,
            'Has cached user:',
            !!cachedUser,
          );
        }

        // If we have token and user in localStorage, refresh initialState
        if (token && cachedUser) {
          if (process.env.NODE_ENV === 'development') {
            console.log(
              'onPageChange - Found auth data in localStorage, refreshing initialState...',
            );
          }

          // Refresh initialState
          setInitialState((s) => ({
            ...s,
            currentUser: s?.currentUser, // Trigger re-fetch
          }));

          // Don't redirect - user is actually logged in
          return;
        }

        // ✅ Clear React state before redirecting to login
        setInitialState((s) => ({
          ...s,
          currentUser: undefined,
          menu: undefined,
        }));

        // ✅ Preserve current location for redirect after login
        const redirectUrl = encodeURIComponent(
          location.pathname + location.search,
        );

        if (process.env.NODE_ENV === 'development') {
          console.log(
            'onPageChange - No auth data found, clearing state and redirecting to login',
          );
          console.log(
            '🔍 APP.TSX: Preserving redirect URL:',
            location.pathname + location.search,
          );
          console.log('🔍 APP.TSX: Encoded redirect URL:', redirectUrl);
        }

        history.push(`${loginPath}?redirect=${redirectUrl}`);
      }
    },
    childrenRender: (children) => (
      <>
        <ConfigProvider
          modal={{ mask: { blur: false } }}
          drawer={{ mask: { blur: false } }}
        >
          <Watermark
            gap={[250, 200]}
            font={{
              color: 'rgba(148,148,148, 0.15)',
            }}
            content={[
              initialState?.currentUser?.name || 'Confidential',
              new Date().toLocaleDateString(),
            ]}
          >
            {children}
          </Watermark>
        </ConfigProvider>
      </>
    ),
    ...initialState?.settings,
    onCollapse: handleCollapse,
    menuExtraRender: (siderProps) => (
      <MenuSearch
        collapsed={Boolean(siderProps?.collapsed)}
        keyword={menuKeyword}
        menuData={siderProps?.menuData}
        onExpand={() => siderProps?.onCollapse?.(false)}
        onKeywordChange={handleMenuKeywordChange}
      />
    ),
    postMenuData: (menuData) => {
      const filtered = filterMenuTree(menuData, menuKeyword);
      if (isSearchingMenu) {
        const nextKeys = collectSubmenuKeys(filtered);
        const currentKeys = openMenuKeysRef.current;
        const keysChanged =
          currentKeys.length !== nextKeys.length ||
          currentKeys.some((key, index) => key !== nextKeys[index]);
        if (keysChanged) {
          openMenuKeysRef.current = nextKeys;
        }
      }
      return filtered;
    },
    menuProps: isSearchingMenu
      ? ({
          get openKeys() {
            return openMenuKeysRef.current;
          },
        } as MenuProps)
      : undefined,
  };
};

/**
 * @name patchClientRoutes
 * @description Automatically add access property to parent routes based on their name
 * This allows dynamic parent menu visibility without manually adding access to routes.ts
 */
export function patchClientRoutes({ routes }: { routes: any[] }) {
  const patchRoutes = (routeList: any[]) => {
    for (const route of routeList) {
      // If route has children and has a name but no access property, add access based on name
      if (route.routes?.length && route.name && !route.access) {
        route.access = route.name;
      }

      // Recurse into nested routes
      if (route.routes) patchRoutes(route.routes);
      if (route.children) patchRoutes(route.children);
    }
  };

  patchRoutes(routes);
}

/**
 * @name request configuration, allows custom error handling
 * It is based on axios and ahooks useRequest, providing a unified network request and error handling solution.
 * @doc https://umijs.org/docs/max/request#configuration
 */
// src/app.tsx
const _useRelativeBaseURL = process.env.REACT_APP_MENU_SOURCE === 'dynamic';

export const request = {
  baseURL: _useRelativeBaseURL ? undefined : appConfig.api.baseUrl,
  // keep timeout and errorConfig...
};
